import { left, right } from 'fp-ts/Either'
import * as R from 'ramda'

import { pipe } from 'fp-ts/function';
import * as TE from 'fp-ts/TaskEither';

import { CareerEnum, PlatformEnum } from '@/types/nftAttribute'
import { runSubqlQuery, safeRunSubqlQuery } from '@/utils/requests'

//
// Types & Interfaces
//

export interface Ability {
  current_level_exp_begin: number
  current_level_exp_end: number
  exp: number
  level: number
}

export type SpiritAttributesResponse = {
  spirit: {
    owner_address: string
    code: string
    stats_refreshed_at: string // 体现的是脑机属性的更新时间
    consume_points: number | null
    award_points: number | null
    dressup_nft_id: number | null
    stats: {
      strength: Ability
      dexterity: Ability
      intellect: Ability
      willpower: Ability
      reputation: Ability & { rank: number }
    }
    extra: {
      balance_fulfilled: boolean
      pill_owner: boolean
      pet_owner: boolean
      pw_discord_level: number
      phala_crowdloaner: boolean
      chaos_dao_member: boolean
      kanaria_holder: boolean
      kusama_king_n_queen: boolean
      invited: boolean
      is_whitelisted: boolean
      occupied_a_crowdloaner_stock: boolean
      occupied_a_kanaria_stock: boolean
      occupied_a_pill_and_pet_stock: boolean
      stats_refreshing_at: string // 调用更新接口的时间
      mcp_requesting_at: string // 体现的是服务端异步任务去请求 MCP 的时间
      mcp_error: {
        mcp_request_error: string
        mcp_response_parse_error: string
        mcp_response_fail: string
      }
    }
    second_suit: {
      description: string | null
      nft_id: null
      status: 'locked' | 'unlocked'
    }
  }
  error?: string
  status: 'ok' | 'error'
}

export enum SupportedPlatforms {
  Discord = 'discord',
  Twitter = 'twitter',
  Github = 'github',
  Forum = 'discourse-phala',
  ETH = 'evm',
  BSC = 'evm',
  Khala = 'substrate',
  Kusama = 'substrate',
  PolkaDot = 'substrate',
}

export type GetIdentityLinkedPlatformsResponse = {
  data: {
    [key in SupportedPlatforms]?: number
  }
  status: 'success' | 'fail'
  error?: string
}

export interface McpSignConfigResponse {
  mcp_sign_messages: {
    for_create_spirit: string
    for_update_spirit: string
  }
  mcp_auth_id: string
  total_spirits_count: number
  error?: string
  status: 'ok' | 'error'
}

interface MCPLinkedQuery {
  data: {
    [key in PlatformEnum]?: number
  }
  status: 'success' | 'fail'
  error?: string
}

export interface LeaderboardItem {
  owner_address: string
  stats: {
    strength: Ability
    dexterity: Ability
    intellect: Ability
    willpower: Ability
    reputation: Ability
  }
}

interface LeaderboardResponse {
  status: 'ok'
  error?: string
  spirits: LeaderboardItem[]
}

export interface MintedOriginOfShellNft {
  owner: string
  rarity: string
  race: string
  career: string
  collectionId: number
  nftId: number
  generation: number
}

//
// Functions
//

// phala_world_server
export async function fetchSpiritAttributes(prefix: string, address: string) {
  try {
    const response = await fetch(`${prefix}/spirits/${address}`)
    const data: SpiritAttributesResponse = await response.json()
    if (data.status === 'ok') {
      return right(data.spirit)
    }
    return left(data.error || 'Unknown error')
  } catch (err) {
    return left(err)
  }
}

export async function createSpirit(
  prefix: string, address: string, mcp_msg_signature: string, invited?: boolean,
  ref_code?: string
) {
  try {
    const response = await fetch(`${prefix}/spirits`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ address, mcp_msg_signature, invited, ref_code })
    })
    if (response.status === 200) {
      const data = await response.json()
      if (data.status === 'ok') {
        return right(data)
      }
      return left(`update spirit data fail: ${data.error}`)
    }
    return left(`${response.status}: ${response.statusText}`)
  } catch (err) {
    return left(err)
  }
}

export async function updateSpirit(prefix: string, address: string, mcp_msg_signature: string) {
  try {
    const response = await fetch(`${prefix}/spirits/${address}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ mcp_msg_signature, })
    })
    if (response.status === 200) {
      const data = await response.json()
      if (data.status === 'ok') {
        return right(data)
      }
      return left(`update spirit data fail: ${data.error}`)
    }
    return left(`${response.status}: ${response.statusText}`)
  } catch (err) {
    return left(`${err}`)
  }
}

/**
 * GET /
 */
export const getMcpSignConfigData = async (prefix: string) => {
  const resp = await fetch(prefix)
  const data: McpSignConfigResponse = await resp.json()
  if (data.status === 'ok') {
    return data
  }
  return null
}

export const getMCPLinkedData = async (prefix: string, address?: string) => {
  if (!address) {
    return left('Please select a wallet first.')
  }
  try {
    const response = await fetch(`${prefix}/api/v1/identity/link/${address}`)
    if (response.status === 200) {
      const data: MCPLinkedQuery = await response.json()
      if (data.status === 'success') {
        return right(data.data)
      }
      return left(`update spirit data fail: ${data.error}`)
    }
    return left(`${response.status}: ${response.statusText}`)
  } catch (err) {
    return left(`${err}`)
  }
}

interface RedeemCodeResponse {
  redeem_code: {
    message: string
    signature: string
  }
  status: 'ok'
  error?: string
}

export const getSpiritTicketData = async (prefix: string, address?: string) => {
  if (!address) {
    return left('Please select a wallet first.')
  }

  try {
    const response = await fetch(`${prefix}/spirits/${address}/redeem_code`, {
      method: 'POST'
    })
    const data: RedeemCodeResponse = await response.json()
    if (data.status === 'ok') {
      return right(data.redeem_code)
    }
    return left(data.error || 'Unknown error')
  } catch (err) {
    return left(err)
  }
}

export async function getSpiritLeaderboard(prefix: string) {
  try {
    const resp = await fetch(`${prefix}/leaderboard`)
    const data: LeaderboardResponse = await resp.json()
    return right(data.spirits)
  } catch (err) {
    return left(`${err}`)
  }
}

export async function bulkGetOriginOfShells(prefix: string, addresses: string[]) {
  try {
    const result = await runSubqlQuery<{
      data: {
        mintedOriginOfShellNfts: {
          nodes: MintedOriginOfShellNft[]
        }
      }
    }>(
      prefix,
      `
        query ($addresses: [String!]) {
          mintedOriginOfShellNfts(filter: {
            owner: { in: $addresses }
          }) {
            nodes {
              owner,
              collectionId,
              nftId,
              rarity,
              career,
              race,
              generation
            }
          }
        }
      `,
      { addresses }
    )
    return right(
      R.pathOr([], ['data', 'mintedOriginOfShellNfts', 'nodes'], result) as MintedOriginOfShellNft[]
    )

  } catch (err) {
    return left(`${err}`)
  }
}

export async function getEarnedEnergyPoints(endpoint: string, address: string) {
  const taskRunner = pipe(
    safeRunSubqlQuery<{
      earnedEnergyPoints: {
        aggregates: {
          sum: {
            points: string
          }
        }
      }
    }>(
      endpoint,
      `
        query Sum($filter: EarnedEnergyPointFilter) {
          earnedEnergyPoints(filter: $filter) {
            aggregates {
              sum {
                points
              }
            }
          }
        }
      `,
      {
        "filter": {
          "identityId": {
            "equalTo": address
          }
        }
      }
    ),
    TE.map(R.pipe(R.pathOr('0', ['earnedEnergyPoints', 'aggregates', 'sum', 'points']), Number)),
  )
  return await taskRunner()
}